import { useMemo, useRef, useState } from 'react';
import { UMIcon, UMInput } from '../../urbanmap/_components';
import { Chip, chipListStyle } from './Chip';
import { UMR, caption } from './tokens';
import type { Commune } from './types';

interface Props {
  communes: Commune[];
  selected: string[];
  onChange: (insee: string[]) => void;
}

const MAX_SUGGESTIONS = 6;
const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/**
 * Recherche de communes avec autocomplétion et sélection multiple.
 * Chaque ajout met à jour la carte (contour de la commune + bâtiments inclus).
 */
export function CommuneSearch({ communes, selected, onChange }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();

  const byInsee = useMemo(() => new Map(communes.map((c) => [c.insee, c])), [communes]);
  const suggestions = useMemo(() => {
    const q = norm(query.trim());
    return communes
      .filter((c) => !selected.includes(c.insee))
      .filter((c) => !q || norm(c.name).includes(q) || c.postalCode.startsWith(q) || c.insee.startsWith(q))
      .slice(0, MAX_SUGGESTIONS);
  }, [communes, selected, query]);

  const add = (insee: string) => {
    onChange([...selected, insee]);
    setQuery('');
    setOpen(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{ position: 'relative' }}
        onFocus={() => { clearTimeout(blurTimer.current); setOpen(true); }}
        onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 120); }}
      >
        <UMInput
          value={query}
          onChange={(v) => { setQuery(v); setOpen(true); }}
          placeholder="Rechercher une commune, un code postal…"
          ariaLabel="Rechercher une commune"
          icon="search"
        />
        {open && suggestions.length > 0 && (
          <ul
            role="listbox"
            aria-label="Communes proposées"
            style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, margin: 0, padding: 0, listStyle: 'none',
              background: UMR.white, border: `1px solid ${UMR.borderInput}`, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 232, overflowY: 'auto',
            }}
          >
            {suggestions.map((c) => (
              <li key={c.insee}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(c.insee)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer',
                    border: 'none', borderBottom: `1px solid ${UMR.borderSubtle}`, background: 'transparent', textAlign: 'left', fontFamily: UMR.font, fontSize: 14,
                  }}
                >
                  <UMIcon name="location_city" size={18} color={UMR.textSecondary} />
                  <span style={{ flex: 1 }}>
                    {c.name} <span style={{ color: UMR.textSecondary }}>({c.postalCode})</span>
                  </span>
                  <span style={{ ...caption, whiteSpace: 'nowrap' }}>
                    {c.available ? '' : 'hors démo'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected.length === 0 ? (
        <div style={caption}>Ajoutez une ou plusieurs communes : le contour de chacune s’affiche sur la carte.</div>
      ) : (
        <ul style={chipListStyle}>
          {selected.map((insee) => {
            const c = byInsee.get(insee);
            return (
              <Chip
                key={insee}
                label={c?.name ?? insee}
                removeLabel={`Retirer ${c?.name ?? insee}`}
                onRemove={() => onChange(selected.filter((x) => x !== insee))}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
}
